import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';

function AdminDashboard() {
  const { gebruiker } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (gebruiker?.rol !== 'super_admin' && gebruiker?.rol !== 'hoofdbeheerder') {
      navigate('/dashboard');
      return;
    }
    loadStats();
  }, [gebruiker, navigate]);

  const loadStats = async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data.stats);
    } catch (e) {
      console.error('Stats fout:', e);
    } finally {
      setLoading(false);
    }
  };

  const formatEuro = (n) => '€' + Number(n).toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  if (loading) return <div style={{ padding: 32 }}>Laden...</div>;
  if (!stats) return <div style={{ padding: 32 }}>Geen data</div>;

  const card = { background: 'white', padding: 24, borderRadius: 8, boxShadow: '0 1px 3px rgba(0,0,0,0.05)' };
  const label = { fontSize: 12, color: '#718096', textTransform: 'uppercase', letterSpacing: 1, fontWeight: 600 };

  return (
    <>
      <h1 style={{ marginTop: 0, marginBottom: 24 }}>Dashboard</h1>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr 1fr', gap: 16, marginBottom: 24 }}>
        <div style={{ ...card, background: 'linear-gradient(135deg, #4a9eff 0%, #2c7be5 100%)', color: 'white' }}>
          <div style={{ ...label, color: 'rgba(255,255,255,0.8)' }}>Maandelijkse omzet (MRR)</div>
          <div style={{ fontSize: 42, fontWeight: 700, marginTop: 8 }}>{formatEuro(stats.mrr)}</div>
          <div style={{ fontSize: 13, opacity: 0.9, marginTop: 4 }}>per maand · excl. trial & enterprise</div>
        </div>
        <div style={card}>
          <div style={label}>Actieve bedrijven</div>
          <div style={{ fontSize: 36, fontWeight: 700, color: '#1a2332', marginTop: 8 }}>{stats.totalen.totaal_actief}</div>
        </div>
        <div style={card}>
          <div style={label}>In trial</div>
          <div style={{ fontSize: 36, fontWeight: 700, color: '#ed8936', marginTop: 8 }}>{stats.totalen.in_trial}</div>
        </div>
        <div style={card}>
          <div style={label}>Forms-only</div>
          <div style={{ fontSize: 36, fontWeight: 700, color: '#9f7aea', marginTop: 8 }}>{stats.totalen.forms_only}</div>
        </div>
      </div>

      <div style={{ ...card, marginBottom: 24 }}>
        <h2 style={{ marginTop: 0, fontSize: 18 }}>Bedrijven per pakket</h2>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #e2e8f0', textAlign: 'left' }}>
              <th style={{ padding: '8px 0', ...label }}>Pakket</th>
              <th style={{ padding: '8px 0', ...label }}>Prijs</th>
              <th style={{ padding: '8px 0', ...label, textAlign: 'right' }}>Aantal</th>
              <th style={{ padding: '8px 0', ...label, textAlign: 'right' }}>Omzet/mnd</th>
            </tr>
          </thead>
          <tbody>
            {stats.per_pakket.map(p => {
              const omzet = parseFloat(p.prijs_per_maand) * parseInt(p.aantal);
              return (
                <tr key={p.naam} style={{ borderBottom: '1px solid #f1f5f9' }}>
                  <td style={{ padding: '12px 0', fontWeight: 600, textTransform: 'capitalize' }}>{p.naam.replace('_', ' ')}</td>
                  <td style={{ padding: '12px 0', color: '#718096' }}>
                    {parseFloat(p.prijs_per_maand) === 0 ? (p.naam === 'enterprise' ? 'op aanvraag' : '—') : formatEuro(p.prijs_per_maand)}
                  </td>
                  <td style={{ padding: '12px 0', textAlign: 'right', fontWeight: 600 }}>{p.aantal}</td>
                  <td style={{ padding: '12px 0', textAlign: 'right', color: '#48bb78', fontWeight: 600 }}>
                    {omzet > 0 ? formatEuro(omzet) : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 16 }}>
        <div style={card}>
          <div style={label}>Openstaande facturen</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#e53e3e', marginTop: 8 }}>{stats.facturen.open_aantal}</div>
          <div style={{ fontSize: 14, color: '#718096', marginTop: 4 }}>{formatEuro(stats.facturen.open_bedrag)}</div>
        </div>
        <div style={card}>
          <div style={label}>Betaalde facturen</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#48bb78', marginTop: 8 }}>{stats.facturen.betaald_aantal}</div>
        </div>
        <div style={card}>
          <div style={label}>Werkorder klanten</div>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#1a2332', marginTop: 8 }}>{stats.totalen.werkorder_klanten}</div>
        </div>
      </div>

      {/* Demo aanvragen sectie */}
      {stats.demo && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 16, marginTop: 24, marginBottom: 16 }}>
            <div style={{ ...card, borderLeft: '4px solid #f59e0b' }}>
              <div style={label}>Nieuwe aanvragen</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#f59e0b', marginTop: 8 }}>{stats.demo.nieuw}</div>
              {parseInt(stats.demo.nieuw) > 0 && (
                <div style={{ fontSize: 12, color: '#f59e0b', marginTop: 4, fontWeight: 600 }}>⚠ Actie nodig</div>
              )}
            </div>
            <div style={card}>
              <div style={label}>In behandeling</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#3b82f6', marginTop: 8 }}>{stats.demo.in_behandeling}</div>
              <div style={{ fontSize: 12, color: '#718096', marginTop: 4 }}>gecontacteerd / demo gepland</div>
            </div>
            <div style={card}>
              <div style={label}>Gewonnen deze maand</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#48bb78', marginTop: 8 }}>{stats.demo.gewonnen_deze_maand}</div>
            </div>
            <div style={card}>
              <div style={label}>Gewonnen totaal</div>
              <div style={{ fontSize: 28, fontWeight: 700, color: '#1a2332', marginTop: 8 }}>{stats.demo.gewonnen}</div>
            </div>
          </div>

          {stats.demo_recent && stats.demo_recent.length > 0 && (
            <div style={{ ...card, padding: 0 }}>
              <div style={{ padding: '16px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <h2 style={{ margin: 0, fontSize: 16 }}>Laatste demo-aanvragen</h2>
                <a href="/admin/demo-aanvragen" style={{ fontSize: 13, color: '#4a9eff', textDecoration: 'none', fontWeight: 600 }}>Bekijk alle →</a>
              </div>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                  {stats.demo_recent.map(d => {
                    const colors = { nieuw: '#f59e0b', gecontacteerd: '#3b82f6', demo_gepland: '#8b5cf6', gewonnen: '#16a34a', afgewezen: '#6b7280' };
                    return (
                      <tr key={d.id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                        <td style={{ padding: '12px 24px', fontSize: 14 }}>
                          <div style={{ fontWeight: 600 }}>{d.naam}</div>
                          {d.bedrijf && <div style={{ fontSize: 12, color: '#718096' }}>{d.bedrijf}</div>}
                        </td>
                        <td style={{ padding: '12px 16px', fontSize: 13, color: '#718096' }}>{d.email}</td>
                        <td style={{ padding: '12px 16px', fontSize: 12, color: '#a0aec0' }}>{d.bron}</td>
                        <td style={{ padding: '12px 16px' }}>
                          <span style={{ background: colors[d.status] || '#a0aec0', color: 'white', padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                            {d.status.replace('_', ' ')}
                          </span>
                        </td>
                        <td style={{ padding: '12px 24px', fontSize: 12, color: '#718096', textAlign: 'right' }}>
                          {new Date(d.created_at).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}
    </>
  );
}

export default AdminDashboard;
